import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import "./SettingsForm.css";

import Navbar from "../components/Navbar/Navbar";
import TopHeader from "../components/Navbar/TopHeader";
import FeatureGate from "../components/FeatureGate";
import generateToastContainer from "../utils/ToastContainer";
import { apiCall } from "../api/apiCall";
import { useTranslation } from "../i18n/UseTranslation";
import { useCurrentUser } from "../expense_report/UserCurrentUser";
import { Company } from "../models/company";
import { getAuth, onAuthStateChanged } from "firebase/auth"; 
import GamificationSettings from "./gamification/GamificationSettings";
import MileageRatesSettings from "./mileageRates/MileageRatesSettings";

type CurrentUser = {
  company_uuid?: string;
  Company?: { Subscription?: { status?: string; plan_code?: string } };
  company?: {
    Subscription?: { status?: string; plan_code?: string };
    subscription?: { status?: string; plan_code?: string };
  };
};

type ActiveTab = "gamification" | "mileageRates";

function SettingsForm() {
  const { t } = useTranslation();
  const translations = t.settingsForm;
  const mt = translations?.mileageRates ?? ({} as Record<string, any>);

  const { isSuperAdmin } = useCurrentUser();

  const [activeTab, setActiveTab] = useState<ActiveTab>("gamification");
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState<string | null>(null);

  const [isAuthReady, setIsAuthReady] = useState(false);
  

  useEffect(() => {
    const auth = getAuth(); 
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

 const { data: me } = useQuery<CurrentUser>({
    queryKey: ["me"],
    queryFn: () => apiCall<CurrentUser>("/users/me"),
    enabled: isAuthReady, 
  });

  const userSubscription =
    me?.Company?.Subscription || me?.company?.Subscription || me?.company?.subscription;

  const currentPlan: "free" | "essential" | "business" | "enterprise" =
    (userSubscription?.status === "active"
      ? userSubscription.plan_code ?? "free"
      : "free") as "free" | "essential" | "business" | "enterprise";

  const companyUuid = isSuperAdmin ? selectedCompanyUuid : me?.company_uuid;

  const { data: allCompanies, isLoading: isLoadingCompanies } = useQuery<Company[], Error>({
    queryKey: ["fetchAllCompanies"],
    queryFn: () => apiCall<Company[]>("/companies", { method: "GET" }),
    enabled: isSuperAdmin && isAuthReady,
  });

  return (
    <div className="dashboard-wrapper">
      <Navbar />
      <TopHeader />

      <main className="settings-page">
        <div className="page-header">
          <h1>{translations?.pageTitle ?? "Settings"}</h1>
        </div>

        {isSuperAdmin && (
          <div className="policy-company-selector settings-card-wide">
            <label className="account-field-label">
              {translations?.selectCompany ?? "Select a company"}
            </label>
            <select
              className="account-select"
              value={selectedCompanyUuid ?? ""}
              onChange={(e) => setSelectedCompanyUuid(e.target.value || null)}
              disabled={isLoadingCompanies}
            >
              <option value="">{mt.nationalDefaultOption ?? "— National / Default —"}</option>
              {(allCompanies ?? []).map((company) => (
                <option key={company.uuid} value={company.uuid}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <FeatureGate
          userPlan={currentPlan}
          requiredPlan="essential"
          featureName={translations?.featureName ?? "settings"}
        >
          <div className="settings-tabs">
            <button
              type="button"
              className={`settings-tab ${activeTab === "gamification" ? "active" : ""}`}
              onClick={() => setActiveTab("gamification")}
            >
              🏆 {translations?.gamification?.title ?? "Gamification"}
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === "mileageRates" ? "active" : ""}`}
              onClick={() => setActiveTab("mileageRates")}
            >
              🚗 {mt.title ?? "Mileage Rates"}
            </button>
          </div>

          {activeTab === "gamification" && (
            <GamificationSettings translations={translations} companyUuid={companyUuid} />
          )}

          {activeTab === "mileageRates" && (
            <MileageRatesSettings mt={mt} companyUuid={companyUuid} isSuperAdmin={isSuperAdmin} />
          )}
        </FeatureGate>

        {generateToastContainer()}
      </main>
    </div>
  );
}

export default SettingsForm;